import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  addToast,
  Button,
  Card,
  CardBody,
  Input,
  Select,
  SelectItem,
} from '@structural-climbing/ui'
import { RemoveCategoryModal } from './RemoveCategoryModal'
import type { CompetitionCategory } from './types'

interface Props {
  competitionId: string
  categories: CompetitionCategory[] | undefined
  /** The competition is over, so its shape is the result and cannot change. */
  locked: boolean
  onRefresh: () => void
  /** Opens the Competitors tab filtered to one category. */
  onShowCompetitors: (categoryId: string) => void
}

interface Draft {
  name: string
  description: string
  min_age: string
  max_age: string
  gender: 'any' | 'male' | 'female'
  capacity: string
}

/** Why the server refused an edit, so the message names the field. */
const EDIT_ERROR_KEYS: Record<string, string> = {
  CATEGORY_GENDER_LOCKED: 'competitions.errorCategoryGenderLocked',
  CATEGORY_AGE_NARROWED: 'competitions.errorCategoryAgeNarrowed',
  CATEGORY_CAPACITY_BELOW_ENTRIES: 'competitions.errorCategoryCapacityBelow',
}

const EMPTY: Draft = {
  name: '',
  description: '',
  min_age: '',
  max_age: '',
  gender: 'any',
  capacity: '',
}

/**
 * Categories say who a competitor is ranked against. Time slots (the Groups
 * tab) say when they climb; the two are deliberately separate, so three
 * categories across two time slots costs nothing.
 */
export function CategoriesTab({
  competitionId,
  categories: given,
  locked,
  onRefresh,
  onShowCompetitors,
}: Props) {
  // Defaulted rather than trusted: a payload missing this field should leave an
  // empty tab, not take the whole competition page down with it.
  const categories = given ?? []
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [draft, setDraft] = useState<Draft>(EMPTY)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [removing, setRemoving] = useState<CompetitionCategory | null>(null)
  const [error, setError] = useState<string | null>(null)
  const formRef = useRef<HTMLDivElement>(null)

  // Once anyone has entered, who the category admits can only stay or widen:
  // eligibility is checked at join time, so narrowing it would strand them.
  const editing = categories.find((c) => c.id === editingId) ?? null
  const entered = editing?.entry_count ?? 0

  function resetForm() {
    setDraft(EMPTY)
    setEditingId(null)
    setError(null)
  }

  function startEditing(category: CompetitionCategory) {
    setDraft({
      name: category.name,
      description: category.description ?? '',
      min_age: category.min_age === null ? '' : String(category.min_age),
      max_age: category.max_age === null ? '' : String(category.max_age),
      gender: category.gender,
      capacity: category.capacity === null ? '' : String(category.capacity),
    })
    setEditingId(category.id)
    setError(null)
    // The form sits below the list, out of sight when the list is long.
    formRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }

  /** Mirrors the server's check, so the common mistake never costs a round trip. */
  function narrowsAge(): boolean {
    if (!editing || entered === 0) return false
    const min = draft.min_age ? Number(draft.min_age) : null
    const max = draft.max_age ? Number(draft.max_age) : null
    const minRises = min !== null && (editing.min_age === null || min > editing.min_age)
    const maxFalls = max !== null && (editing.max_age === null || max < editing.max_age)
    return minRises || maxFalls
  }

  async function saveCategory() {
    if (!draft.name.trim()) {
      setError(t('competitions.errorCategoryName'))
      return
    }
    if (narrowsAge()) {
      setError(t('competitions.errorCategoryAgeNarrowed'))
      return
    }
    if (editing && entered > 0 && draft.capacity && Number(draft.capacity) < entered) {
      setError(t('competitions.errorCategoryCapacityBelow', { count: entered }))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      const fields = {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        min_age: draft.min_age ? Number(draft.min_age) : null,
        max_age: draft.max_age ? Number(draft.max_age) : null,
        gender: draft.gender,
        capacity: draft.capacity ? Number(draft.capacity) : null,
      }
      // Editing keeps the category's place in the list, so sort_order is left alone.
      const res = editingId
        ? await fetch(`${apiUrl}/competition-categories/${editingId}`, {
            method: 'PATCH',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify(fields),
          })
        : await fetch(`${apiUrl}/competitions/${competitionId}/categories`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...fields, sort_order: categories.length }),
          })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          error?: string
          code?: string
          entered?: number
        }
        const key = body.code ? EDIT_ERROR_KEYS[body.code] : undefined
        setError(
          key
            ? t(key, { count: body.entered ?? entered })
            : (body.error ?? t('competitions.errorSaveFailed')),
        )
        return
      }
      addToast({
        title: t(editingId ? 'competitions.categoryUpdated' : 'competitions.categoryAdded'),
        color: 'success',
      })
      resetForm()
      onRefresh()
    } finally {
      setSaving(false)
    }
  }

  const ageHintKey =
    entered > 0 ? 'competitions.categoryAgeWidenOnly' : 'competitions.categoryAgeHint'

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-foreground/60">{t('competitions.categoriesHint')}</p>

      {categories.length === 0 ? (
        <Card className="shadow-sm">
          <CardBody className="py-6 text-center text-foreground/60">
            {t('competitions.categoriesEmpty')}
          </CardBody>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2">
          {categories.map((category) => (
            <li
              key={category.id}
              className={`flex items-center justify-between gap-4 rounded-lg border p-3 ${
                category.id === editingId ? 'border-primary' : 'border-default-200'
              }`}
            >
              <div className="min-w-0">
                <p className="font-medium">{category.name}</p>
                <p className="text-sm text-foreground/60">
                  {ageLabel(category, t)} · {genderLabel(category.gender, t)}
                  {category.capacity !== null &&
                    ` · ${t('competitions.categoryPlaces', { count: category.capacity })}`}
                  {' · '}
                  {t('competitions.categoryEntrants', { count: category.entry_count })}
                </p>
                {category.description && (
                  <p className="text-sm text-foreground/50">{category.description}</p>
                )}
              </div>
              {!locked && (
                <div className="flex shrink-0 gap-1">
                  <Button size="sm" variant="light" onPress={() => startEditing(category)}>
                    {t('common.edit')}
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    color="danger"
                    onPress={() => setRemoving(category)}
                  >
                    {t('common.remove')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Adding a category to a finished competition would rewrite a result
          competitors have already seen. */}
      {locked ? (
        <p className="rounded-lg bg-content2 p-3 text-sm text-foreground/60">
          {t('competitions.endedLockNote')}
        </p>
      ) : (
        <Card className="shadow-sm" ref={formRef}>
          <CardBody className="flex flex-col gap-3">
            <h3 className="font-semibold">
              {t(editingId ? 'competitions.editCategory' : 'competitions.addCategory')}
            </h3>
            <Input
              label={t('competitions.categoryNameLabel')}
              placeholder={t('competitions.categoryNamePlaceholder')}
              value={draft.name}
              onValueChange={(v) => setDraft({ ...draft, name: v })}
            />
            <Input
              label={t('competitions.categoryDescriptionLabel')}
              description={t('competitions.categoryDescriptionHint')}
              value={draft.description}
              onValueChange={(v) => setDraft({ ...draft, description: v })}
            />
            {/* Same grid as SessionsTab, and no width caps: a narrow box makes
                HeroUI's floating label wrap onto the border. */}
            <div className="grid gap-3 sm:grid-cols-2">
              <Input
                type="number"
                min={0}
                label={t('competitions.categoryMinAgeLabel')}
                description={t(ageHintKey)}
                value={draft.min_age}
                onValueChange={(v) => setDraft({ ...draft, min_age: v })}
              />
              <Input
                type="number"
                min={0}
                label={t('competitions.categoryMaxAgeLabel')}
                description={t(ageHintKey)}
                value={draft.max_age}
                onValueChange={(v) => setDraft({ ...draft, max_age: v })}
              />
              <Select
                label={t('competitions.categoryGenderLabel')}
                selectedKeys={[draft.gender]}
                isDisabled={entered > 0}
                description={
                  entered > 0
                    ? t('competitions.categoryGenderLocked', { count: entered })
                    : undefined
                }
                onSelectionChange={(keys) => {
                  // Clicking the selected item again hands back an empty set.
                  const val = [...keys][0]
                  if (val) setDraft({ ...draft, gender: val as Draft['gender'] })
                }}
              >
                <SelectItem key="any" textValue={t('competitions.categoryGenderAny')}>
                  {t('competitions.categoryGenderAny')}
                </SelectItem>
                <SelectItem key="male" textValue={t('competitions.categoryGenderMale')}>
                  {t('competitions.categoryGenderMale')}
                </SelectItem>
                <SelectItem key="female" textValue={t('competitions.categoryGenderFemale')}>
                  {t('competitions.categoryGenderFemale')}
                </SelectItem>
              </Select>
              <Input
                type="number"
                min={Math.max(entered, 1)}
                label={t('competitions.categoryCapacityLabel')}
                description={
                  entered > 0
                    ? t('competitions.categoryCapacityMinEntries', { count: entered })
                    : t('competitions.categoryCapacityHint')
                }
                value={draft.capacity}
                onValueChange={(v) => setDraft({ ...draft, capacity: v })}
              />
            </div>

            {error && <p className="text-sm text-danger">{error}</p>}

            <div className="flex gap-2">
              <Button color="primary" isLoading={saving} onPress={() => void saveCategory()}>
                {t(editingId ? 'common.save' : 'competitions.addCategory')}
              </Button>
              {editingId && (
                <Button variant="light" onPress={resetForm}>
                  {t('common.cancel')}
                </Button>
              )}
            </div>
          </CardBody>
        </Card>
      )}

      <RemoveCategoryModal
        category={removing}
        label={removing ? categoryLabel(removing, t) : ''}
        onClose={() => setRemoving(null)}
        onRemoved={() => {
          if (removing?.id === editingId) resetForm()
          setRemoving(null)
          onRefresh()
        }}
        onShowCompetitors={(id) => {
          setRemoving(null)
          onShowCompetitors(id)
        }}
      />
    </div>
  )
}

type Translate = ReturnType<typeof useTranslation>['t']

function ageLabel(category: CompetitionCategory, t: Translate): string {
  const { min_age: min, max_age: max } = category
  if (min !== null && max !== null) return t('competitions.categoryAgeRange', { min, max })
  if (min !== null) return t('competitions.categoryAgeMin', { min })
  if (max !== null) return t('competitions.categoryAgeMax', { max })
  return t('competitions.categoryAgeAny')
}

function genderLabel(gender: string, t: Translate): string {
  if (gender === 'male') return t('competitions.categoryGenderMale')
  if (gender === 'female') return t('competitions.categoryGenderFemale')
  return t('competitions.categoryGenderAny')
}

/** Two categories often share a name and differ by gender, so say which. */
function categoryLabel(category: CompetitionCategory, t: Translate): string {
  return category.gender === 'any'
    ? category.name
    : `${category.name} (${genderLabel(category.gender, t)})`
}
